import { ptBrCommon } from '@/lib/pt-br/messages';
import { updateFormState } from '@/components/auth/forgot-password';
import { initialOtpState, optAtom } from '@/components/otp/atom';
import { useModalAction } from '@/components/ui/modal/modal.context';
import { Routes } from '@/config/routes';
import client from '@/framework/client';
import { API_ENDPOINTS } from '@/framework/client/api-endpoints';
import { setAuthCredentials } from '@/framework/utils/auth-utils';
import { clearAuthCookies } from '@/lib/auth/auth-cookies';
import {
  NEWSLETTER_POPUP_MODAL_KEY,
  REVIEW_POPUP_MODAL_KEY,
} from '@/lib/constants';
import { REC_SESSION_ID_KEY } from '@/lib/events/session-id';
import { faroTrackLoginFailed } from '@/lib/grafana-faro-events';
import { gtmLogin, gtmRegister, gtmUpdateProfile, isMobileDevice } from '@/lib/gtm';
import { useToken } from '@/lib/hooks/use-token';
import { authorizationAtom } from '@/store/authorization-atom';
import { clearCheckoutAtom } from '@/store/checkout';
import type {
  AuthResponse,
  AppleMobileLoginPayload,
  AppleMobileLoginResponse,
  ChangePasswordUserInput,
  OtpLoginInputType,
  RegisterUserInput,
} from '@/types';
import axios from 'axios';
import { useAtom } from 'jotai';
import Cookies from 'js-cookie';
import { useStateMachine } from 'little-state-machine';
import { useRouter } from 'next/router';
import { useEffect, useState, useRef } from 'react';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { toast } from 'react-toastify';
import { userAtom } from './user-store';
import { clearRecipeCheckoutAtom } from '@/store/checkout-recipe';
import {
  updateUserProfile,
  setUserIdentity,
  login as netcoreLogin,
  userToNetcoreProfile,
} from '@/lib/utils/netcore';
import { runPostLoginCartSync } from '@/lib/cart-sync/run-post-login-cart-sync';

interface IOptions {
  enabled?: boolean;
  onSuccess?: () => void;
}

// Track Netcore initialization per user to prevent duplicate calls
// Use both sessionStorage and in-memory flag for reliability
const NETCORE_INIT_KEY = 'netcore_initialized_cpf';
const NETCORE_LOGIN_TRIGGER_KEY = 'netcore_login_trigger_reason';
const NETCORE_FIRST_LAUNCH_SYNC_KEY = 'netcore_first_launch_synced';
let netcoreInitializedCpf: string | null = null;

type NetcoreLoginTriggerReason = 'login' | 'signup' | 'social';

const setNetcoreLoginTrigger = (reason: NetcoreLoginTriggerReason) => {
  try {
    sessionStorage.setItem(NETCORE_LOGIN_TRIGGER_KEY, reason);
  } catch (error) {
    // Silent fail
  }
};

const consumeNetcoreLoginTrigger = (): NetcoreLoginTriggerReason | null => {
  try {
    const reason = sessionStorage.getItem(NETCORE_LOGIN_TRIGGER_KEY);
    if (reason === 'login' || reason === 'signup' || reason === 'social') {
      sessionStorage.removeItem(NETCORE_LOGIN_TRIGGER_KEY);
      return reason;
    }
  } catch (error) {
    // Silent fail
  }

  return null;
};

// Initialize Netcore for a user (only called once per user session)
const initializeNetcoreForUser = (userIdentity: string, userData: any): boolean => {
  // Check both sessionStorage and in-memory flag - skip if EITHER matches
  const storedCpf = sessionStorage.getItem(NETCORE_INIT_KEY);
  
  // Skip if already initialized for this user (check both)
  if (storedCpf === userIdentity || netcoreInitializedCpf === userIdentity) {
    return false;
  }

  // Set identity and login to Netcore (web SDK) - only once
  setUserIdentity(userIdentity);
  netcoreLogin(userIdentity);
  
  // Update user profile (web SDK) - only once
  const netcoreProfile = userToNetcoreProfile(userData);
  updateUserProfile(netcoreProfile);

  // Notify native app via postMessage (same pattern as Google/Apple login) - only once
  try {
    if ((window as any).ReactNativeWebView?.postMessage) {
      (window as any).ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'SET_USER_IDENTITY',
          userIdentity: userIdentity,
        }),
      );
      
      (window as any).ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'USER_LOGIN',
          cpf: userIdentity,
        }),
      );
      
      (window as any).ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'UPDATE_USER_PROFILE',
          userProfile: netcoreProfile,
        }),
      );
    }
  } catch (error) {
    // Silent fail - native app might not be available
  }
  
  // Mark as initialized (both storage and memory) - only after successful initialization
  sessionStorage.setItem(NETCORE_INIT_KEY, userIdentity);
  netcoreInitializedCpf = userIdentity;
  return true;
};

export function useUser({ enabled = true, onSuccess }: IOptions = {}) {
  const [isAuthorized] = useAtom(authorizationAtom);
  const [_, setAtomUser] = useAtom(userAtom);
  const { setEmailVerified, getEmailVerified } = useToken();
  const { emailVerified } = getEmailVerified();
  const router = useRouter();
  const netcoreInitializedRef = useRef<string | null>(null);

  const { data, isLoading, isRefetching, error, isFetchedAfterMount, refetch } =
    useQuery([API_ENDPOINTS.USERS_ME], client.users.me, {
      enabled: isAuthorized && enabled,
      retry: false,
      // Dedup remounts across the ~40 components that call useUser(): without
      // this, any component unmount/remount inside the staleTime window would
      // refire GET /me. Mutations still call queryClient.invalidateQueries(USERS_ME)
      // to refresh after profile/address changes.
      staleTime: 5 * 60 * 1000,
      onSuccess: (data) => {
        setAtomUser(data);
        onSuccess?.();

        // Send GTM login event when user data is available (only once per session)
        if (data && (data.cpf || data.document) && data.email) {
          const loginEventSent = sessionStorage.getItem('gtm_login_sent');
          if (!loginEventSent) {
            gtmLogin({
              cpf: data.cpf || data.document || '',
              email: data.email || '',
            });
            sessionStorage.setItem('gtm_login_sent', 'true');
          }
        }

        if (emailVerified === false) {
          setEmailVerified(true);
          router.reload();
          return;
        }
      },
      onError: (err) => {
        if (axios.isAxiosError(err)) {
          if (err?.response?.status === 409) {
            setEmailVerified(false);
            router.push(Routes.verifyEmail);
            return;
          }
          if (router.pathname === Routes.verifyEmail) {
            return;
          }
        }
      },
    });

  useEffect(() => {
    isAuthorized && data && setAtomUser(data);
    !isAuthorized && setAtomUser(null);
  }, [isAuthorized, data]);

  // Netcore login should run only for explicit auth actions or on first native app launch.
  useEffect(() => {
    if (!data) return;
    
    const userIdentity = data.cpf || data.document;
    if (!userIdentity) return;

    const hasExplicitLoginTrigger = Boolean(
      sessionStorage.getItem(NETCORE_LOGIN_TRIGGER_KEY),
    );
    const isNativeApp = Boolean((window as any).ReactNativeWebView?.postMessage);
    const hasFirstLaunchSync = Boolean(
      sessionStorage.getItem(NETCORE_FIRST_LAUNCH_SYNC_KEY),
    );
    const shouldSyncOnFirstNativeAppLaunch = isNativeApp && !hasFirstLaunchSync;
    const shouldInitialize =
      hasExplicitLoginTrigger || shouldSyncOnFirstNativeAppLaunch;

    if (!shouldInitialize) {
      return;
    }

    // Check both ref and sessionStorage to prevent duplicates for the same user
    const storedCpf = sessionStorage.getItem(NETCORE_INIT_KEY);
    if (netcoreInitializedRef.current === userIdentity || storedCpf === userIdentity) {
      consumeNetcoreLoginTrigger();
      if (shouldSyncOnFirstNativeAppLaunch) {
        sessionStorage.setItem(NETCORE_FIRST_LAUNCH_SYNC_KEY, 'true');
      }
      return; // Already initialized for this user
    }

    // Consume explicit trigger and initialize once for this authenticated user.
    consumeNetcoreLoginTrigger();
    const wasInitialized = initializeNetcoreForUser(userIdentity, data);
    if (wasInitialized) {
      netcoreInitializedRef.current = userIdentity;
      if (shouldSyncOnFirstNativeAppLaunch) {
        sessionStorage.setItem(NETCORE_FIRST_LAUNCH_SYNC_KEY, 'true');
      }
    }
  }, [data?.cpf, data?.document]); // Only depend on CPF/document, not entire data object

  //TODO: do some improvement here
  return {
    me: data,
    isLoading,
    isRefetchingUser: isRefetching,
    refetchUser: refetch,
    error,
    isAuthorized,
    isFetchedAfterMount,
  };
}

export const useCreateAddress = () => {
  const { closeModal } = useModalAction();
  const queryClient = useQueryClient();
  return useMutation(client.users.createAddress, {
    onSuccess: (data) => {
      if (data) {
        toast.success('successfully-address-created');
        closeModal();
        return;
      }
    },
    onError: (error) => {
      const {
        response: { data },
      }: any = error ?? {};

      toast.error(data?.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries(API_ENDPOINTS.USERS_ME);
    },
  });
};

export const useUpdateAddress = () => {
  const { closeModal } = useModalAction();
  const queryClient = useQueryClient();
  return useMutation(client.users.updateAddress, {
    onSuccess: (data) => {
      if (data) {
        toast.success('successfully-address-updated');
        closeModal();
        return;
      }
    },
    onError: (error) => {
      const {
        response: { data },
      }: any = error ?? {};

      toast.error(data?.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries(API_ENDPOINTS.USERS_ME);
    },
  });
};

export const useDeleteAddress = () => {
  const { closeModal } = useModalAction();
  const queryClient = useQueryClient();
  return useMutation(client.users.deleteAddress, {
    onSuccess: (data) => {
      if (data) {
        toast.success('successfully-address-deleted');
        closeModal();
        return;
      }
    },
    onError: (error) => {
      const {
        response: { data },
      }: any = error ?? {};

      toast.error(data?.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries(API_ENDPOINTS.USERS_ME);
    },
  });
};

export const useUpdateEmail = () => {
  const queryClient = useQueryClient();
return useMutation(client.users.updateEmail, {
    onSuccess: (data) => {
      if (data) {
        toast.success("E-mail atualizado com sucesso");
      }
    },
    onError: (error) => {
      const {
        response: { data },
      }: any = error ?? {};

      toast.error(data?.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries(API_ENDPOINTS.USERS_ME);
    },
  });
};

export const useUpdateUser = (options?: { showToast?: boolean; closeModal?: boolean }) => {
const queryClient = useQueryClient();
  const { closeModal } = useModalAction();
  const showToast = options?.showToast !== false; // Default to true
  const shouldCloseModal = options?.closeModal !== false; // Default to true
  
  return useMutation(client.users.update, {
    onSuccess: (data, variables) => {
      if (showToast) {
        toast.success(`${"Perfil atualizado com sucesso"}`);
      }
      
      // Update user profile in Netcore
      if (variables) {
        const netcoreProfile = userToNetcoreProfile(variables);
        updateUserProfile(netcoreProfile);
        
        // Send GTM update_profile event
        if (variables.cpf || variables.document) {
          gtmUpdateProfile({
            cpf: variables.cpf || variables.document || '',
            email: variables.email || '',
            first_name: variables.first_name || '',
            last_name: variables.last_name || '',
            gender: variables.gender || '',
            phone: variables.phone || variables.cellphone || '',
            social_name: variables.social_name || '',
            birth_date: variables.birth_date || '',
            mobile: isMobileDevice(),
          });
        }
        
        // Notify native app of profile update
        try {
          if ((window as any).ReactNativeWebView?.postMessage) {
            (window as any).ReactNativeWebView.postMessage(
              JSON.stringify({
                type: 'UPDATE_USER_PROFILE',
                userProfile: netcoreProfile,
              }),
            );
          }
        } catch (error) {
          // Silent fail
        }
      }
      
      if (shouldCloseModal) {
        closeModal();
      }
    },
    onError: (error) => {
      if (showToast) {
        toast.error(`${"Algo deu errado"}`);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(API_ENDPOINTS.USERS_ME);
    },
  });
};

export const useContact = ({ reset }: { reset: () => void }) => {
return useMutation(client.users.contactUs, {
    onSuccess: (data) => {
      if (data.success) {
        toast.success(`${ptBrCommon(data.message)}`);
        reset();
      } else {
        toast.error(`${ptBrCommon(data.message)}`);
      }
    },
    onError: (err) => {
      console.log(err);
    },
  });
};

export function useLogin() {
  const router = useRouter();
  const [_, setAuthorized] = useAtom(authorizationAtom);
  const { closeModal } = useModalAction();
  const queryClient = useQueryClient();
  let [serverError, setServerError] = useState<string | null>(null);

  const { mutate, mutateAsync, isLoading } = useMutation(client.users.login, {
    onSuccess: async (data, variables) => {
      const { email = '' } = variables;
      if (!data.token) {
        setServerError('error-credential-wrong');
        faroTrackLoginFailed({
          method: 'email_password',
          reason: 'invalid_credentials',
        });
        return;
      }
      setAuthCredentials(data.token, data.permissions, data.refresh_token);
      await runPostLoginCartSync();
      setAuthorized(true);
      setNetcoreLoginTrigger('login');

      // Netcore: User identity and profile will be set when user data is fetched via useUser hook
      // (AuthResponse doesn't include user data, so we wait for useUser to fetch it)
      // registerUser will also be called from useUser hook with CPF
      // GTM login event will be sent from useUser hook when user data is available

      const redirectTarget = sessionStorage.getItem('redirect_after_login');
      if (redirectTarget) {
        sessionStorage.removeItem('redirect_after_login');
        router.push(redirectTarget);
      }

      closeModal();
    },
    onError: (error: any) => {
      const code = error?.response?.data?.error?.code;
      if (code === 'NEEDS_PASSWORD_SETUP') {
        setServerError('error-needs-password-setup');
      } else {
        setServerError('error-credential-wrong');
      }
      faroTrackLoginFailed({
        method: 'email_password',
        reason: code ?? 'request_failed',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries(API_ENDPOINTS.NOTIFY_LOGS);
    },
  });

  return { mutate, mutateAsync, isLoading, serverError, setServerError };
}

export function useSocialLogin() {
  const queryClient = useQueryClient();
  const [_, setAuthorized] = useAtom(authorizationAtom);

  return useMutation(client.users.socialLogin, {
    onSuccess: async (data) => {
      if (data?.token && data?.permissions?.length) {
        setAuthCredentials(
          data.token,
          data.permissions,
          data.refresh_token,
        );
        await runPostLoginCartSync();
        setAuthorized(true);
        setNetcoreLoginTrigger('social');

        // Netcore: User identity and profile will be set when user data is fetched via useUser hook
        // (AuthResponse doesn't include user data, so we wait for useUser to fetch it)
        // GTM login event will be sent from useUser hook when user data is available

        return;
      }
      if (!data.token) {
        toast.error(`${"As credenciais estão incorretas!"}`);
        faroTrackLoginFailed({
          method: 'social',
          reason: 'invalid_credentials',
        });
      }
    },
    onError: () => {
      faroTrackLoginFailed({
        method: 'social',
        reason: 'request_failed',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries(API_ENDPOINTS.NOTIFY_LOGS);
      queryClient.clear();
    },
  });
}

export function useGoogleMobileLogin() {
  const router = useRouter();
  const [_, setAuthorized] = useAtom(authorizationAtom);
  const { closeModal } = useModalAction();
  const queryClient = useQueryClient();
  let [serverError, setServerError] = useState<string | null>(null);

  const { mutate, mutateAsync, isLoading } = useMutation<
    AuthResponse,
    Error,
    { idToken: string; userEmail?: string }
  >(
    (variables) =>
      client.users.googleMobileLogin({ idToken: variables.idToken }),
    {
      onSuccess: async (data, variables) => {
        const { userEmail = '' } = variables;
        if (!data.token) {
          setServerError('error-credential-wrong');
          faroTrackLoginFailed({
            method: 'google_mobile',
            reason: 'invalid_credentials',
          });
          return;
        }
        setAuthCredentials(
          data.token,
          data.permissions,
          data.refresh_token,
        );
        await runPostLoginCartSync();
        setAuthorized(true);
        setNetcoreLoginTrigger('social');

        // Netcore: User identity and profile will be set when user data is fetched via useUser hook
        // (AuthResponse does not include user data, so we wait for useUser to fetch it)
        // registerUser will also be called from useUser hook with CPF
        // GTM login event will be sent from useUser hook when user data is available

        const redirectTarget = sessionStorage.getItem('redirect_after_login');
        if (redirectTarget) {
          sessionStorage.removeItem('redirect_after_login');
          router.push(redirectTarget);
        }

        closeModal();
      },
      onError: () => {
        setServerError('error-credential-wrong');
        faroTrackLoginFailed({
          method: 'google_mobile',
          reason: 'request_failed',
        });
      },
      onSettled: () => {
        queryClient.invalidateQueries(API_ENDPOINTS.NOTIFY_LOGS);
      },
    },
  );

  return { mutate, mutateAsync, isLoading, serverError, setServerError };
}

export function useAppleMobileLogin() {
  const router = useRouter();
  const [_, setAuthorized] = useAtom(authorizationAtom);
  const { closeModal } = useModalAction();
  const queryClient = useQueryClient();
  let [serverError, setServerError] = useState<string | null>(null);

  const { mutate, mutateAsync, isLoading } = useMutation<
    AppleMobileLoginResponse,
    Error,
    AppleMobileLoginPayload
  >(
    (variables) =>
      client.users.appleMobileLogin({
        idToken: variables.idToken,
        userEmail: variables.userEmail,
      }),
    {
      onSuccess: async (data, variables) => {
        const { userEmail = '' } = variables;

        if (!data.token) {
          setServerError('error-credential-wrong');
          faroTrackLoginFailed({
            method: 'apple_mobile',
            reason: 'invalid_credentials',
          });
          return;
        }

        // Apply authentication
        setAuthCredentials(
          data.token,
          data.permissions,
          data.refresh_token,
        );
        await runPostLoginCartSync();
        setAuthorized(true);
        setNetcoreLoginTrigger('social');

        // Netcore: User identity and profile will be set when user data is fetched via useUser hook
        // (AuthResponse doesn't include user data, so we wait for useUser to fetch it)
        // registerUser will also be called from useUser hook with CPF
        // GTM login event will be sent from useUser hook when user data is available

        // Handle redirect
        const redirectTarget = sessionStorage.getItem('redirect_after_login');
        if (redirectTarget) {
          sessionStorage.removeItem('redirect_after_login');
          router.push(redirectTarget);
        }

        closeModal();
      },
      onError: () => {
        setServerError('error-credential-wrong');
        faroTrackLoginFailed({
          method: 'apple_mobile',
          reason: 'request_failed',
        });
      },
      onSettled: () => {
        queryClient.invalidateQueries(API_ENDPOINTS.NOTIFY_LOGS);
      },
    },
  );

  return { mutate, mutateAsync, isLoading, serverError, setServerError };
}

export function useSendOtpCode({
  verifyOnly,
}: Partial<{ verifyOnly: boolean }> = {}) {
  let [serverError, setServerError] = useState<string | null>(null);
  const [otpState, setOtpState] = useAtom(optAtom);

  const { mutate, isLoading } = useMutation(client.users.sendOtpCode, {
    onSuccess: (data) => {
      if (!data.success) {
        setServerError(data.message!);
        return;
      }
      setOtpState({
        ...otpState,
        otpId: data?.id!,
        isContactExist: data?.is_contact_exist!,
        phoneNumber: data?.phone_number!,
        step: data?.is_contact_exist! ? 'OtpForm' : 'RegisterForm',
        ...(verifyOnly && { step: 'OtpForm' }),
      });
    },
    onError: (error: Error) => {
      console.log(error.message);
    },
  });

  return { mutate, isLoading, serverError, setServerError };
}

export function useVerifyOtpCode({
  onVerifySuccess,
}: {
  onVerifySuccess: Function;
}) {
  const [otpState, setOtpState] = useAtom(optAtom);
  let [serverError, setServerError] = useState<string | null>(null);
  const { mutate, isLoading } = useMutation(client.users.verifyOtpCode, {
    onSuccess: (data) => {
      if (!data.success) {
        setServerError(data?.message!);
        return;
      }
      if (onVerifySuccess) {
        onVerifySuccess({
          phone_number: otpState.phoneNumber,
        });
      }
      setOtpState({
        ...initialOtpState,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  return { mutate, isLoading, serverError, setServerError };
}

export function useOtpLogin() {
  const [otpState, setOtpState] = useAtom(optAtom);
const [_, setAuthorized] = useAtom(authorizationAtom);
  const { closeModal } = useModalAction();
  const { setToken } = useToken();
  const queryClient = new QueryClient();
  let [serverError, setServerError] = useState<string | null>(null);

  const { mutate: otpLogin, isLoading } = useMutation(client.users.OtpLogin, {
    onSuccess: async (data) => {
      if (!data.token) {
        setServerError('text-otp-verify-failed');
        faroTrackLoginFailed({
          method: 'otp',
          reason: 'invalid_credentials',
        });
        return;
      }
      setToken(data.token!);
      await runPostLoginCartSync();
      setAuthorized(true);

      // Netcore: User identity and profile will be set when user data is fetched via useUser hook
      // (AuthResponse doesn't include user data, so we wait for useUser to fetch it)
      // GTM login event will be sent from useUser hook when user data is available

      setOtpState({
        ...initialOtpState,
      });
      closeModal();
    },
    onError: () => {
      faroTrackLoginFailed({
        method: 'otp',
        reason: 'request_failed',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries(API_ENDPOINTS.NOTIFY_LOGS);
      queryClient.clear();
    },
  });

  function handleSubmit(input: OtpLoginInputType) {
    otpLogin({
      ...input,
      phone_number: otpState.phoneNumber,
      otp_id: otpState.otpId!,
    });
  }

  return { mutate: handleSubmit, isLoading, serverError, setServerError };
}

type RegisterFormErrors = {
  [K in keyof RegisterUserInput]?: string | string[];
} & {
  /** Marker for account-already-exists recovery UI — not a form field */
  _account_exists?: string;
};

export function useRegister() {
  const queryClient = useQueryClient();
  const [_, setAuthorized] = useAtom(authorizationAtom);
  const { closeModal } = useModalAction();
  let [formError, setFormError] = useState<RegisterFormErrors | null>(null);
  const router = useRouter();

  const login = useMutation(client.users.login, {
    onSuccess: async (data, variables) => {
      setAuthCredentials(
        data.token,
        data.permissions,
        data.refresh_token,
      );
      await runPostLoginCartSync();
      setAuthorized(true);

      // Netcore: User identity and profile will be set when user data is fetched via useUser hook
      // (AuthResponse doesn't include user data, so we wait for useUser to fetch it)
      // registerUser will also be called from useUser hook with CPF
      // GTM login event will be sent from useUser hook when user data is available

      const redirectTarget = sessionStorage.getItem('redirect_after_login');
      if (redirectTarget) {
        sessionStorage.removeItem('redirect_after_login');
        router.push(redirectTarget);
      } else {
        const { pathname } = router;
        router.replace(pathname, undefined, { shallow: true });
      }

      closeModal();
    },
  });

  const { mutate, isLoading } = useMutation(client.users.register, {
    onSuccess: (data, variables) => {
      // Send GTM register event
      gtmRegister({
        cpf: variables.cpf,
        email: variables.email,
        first_name: variables.first_name,
        last_name: variables.last_name,
        gender: variables.gender,
        phone: variables.cellphone || variables.phone || '',
        social_name: variables.social_name || '',
        birth_date: variables.birth_date,
        mobile: isMobileDevice(),
      });

      // Netcore: Initialize using CPF from registration
      // We have the CPF immediately, so we can initialize right away
      const userIdentity = variables.cpf;
      if (userIdentity) {
        setNetcoreLoginTrigger('signup');
        // Use the centralized initialization function to prevent duplicates
        // This will handle setUserIdentity, login, and updateUserProfile all at once
        initializeNetcoreForUser(userIdentity, {
          first_name: variables.first_name,
          last_name: variables.last_name,
          email: variables.email,
          cellphone: variables.cellphone,
          phone: variables.phone,
          gender: variables.gender,
          birth_date: variables.birth_date,
          cpf: variables.cpf,
        });
      }
      // Note: useUser will check if already initialized and skip duplicate calls

      login.mutate({
        email: variables.email,
        password: variables.password,
      });
    },
    onError: (error) => {
      const { response = {} }: any = error ?? {};
      const { data = {} } = response;

      const errorData = data.error || data;
      const code = errorData?.code;
      const message = errorData?.message;
      const details = errorData?.details;

      if (code === 'EMAIL_ALREADY_IN_USE') {
        setFormError({ _account_exists: code });
        return;
      }

      if (details && Array.isArray(details) && details.length > 0) {
        toast.error(message);

        const fieldErrors: any = {};
        details.forEach((detail: any) => {
          if (detail.field) {
            fieldErrors[detail.field] = [''];
          }
        });
        setFormError(fieldErrors);
      } else if (message) {
        toast.error(message);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(API_ENDPOINTS.NOTIFY_LOGS);
    },
  });

  return { mutate, isLoading, formError, setFormError };
}

export function useResendVerificationEmail() {
const { mutate, isLoading } = useMutation(
    client.users.resendVerificationEmail,
    {
      onSuccess: (data) => {
        if (data?.success) {
          toast.success("E-mail enviado com sucesso");
        }
      },
      onError: (error) => {
        const {
          response: { data },
        }: any = error ?? {};

        toast.error(data?.message);
      },
    },
  );

  return { mutate, isLoading };
}
export function useLogout() {
  const queryClient = useQueryClient();
  const [_, setAuthorized] = useAtom(authorizationAtom);
  const [_r, resetCheckout] = useAtom(clearCheckoutAtom);
  const [, resetRecipeCheckout] = useAtom(clearRecipeCheckoutAtom);

  const performCleanup = () => {
    clearAuthCookies();
    Cookies.remove(REVIEW_POPUP_MODAL_KEY);
    Cookies.remove(NEWSLETTER_POPUP_MODAL_KEY);
    setAuthorized(false);
    
    // Clear Netcore initialization tracking (both storage and memory)
    sessionStorage.removeItem(NETCORE_INIT_KEY);
    sessionStorage.removeItem(NETCORE_LOGIN_TRIGGER_KEY);
    sessionStorage.removeItem(NETCORE_FIRST_LAUNCH_SYNC_KEY);
    netcoreInitializedCpf = null;
    
    // Clear GTM login event flag
    sessionStorage.removeItem('gtm_login_sent');

    // Clear recommendation/analytics session ID for identity stitching
    localStorage.removeItem(REC_SESSION_ID_KEY);

    // Notify native app of logout via postMessage (same pattern as login)
    try {
      if ((window as any).ReactNativeWebView?.postMessage) {
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify({
            type: 'USER_LOGOUT',
          }),
        );
      }
    } catch (error) {
      // Silent fail - native app might not be available
    }
    
    //@ts-ignore
    resetCheckout();
    //@ts-ignore
    resetRecipeCheckout();
    queryClient.removeQueries(API_ENDPOINTS.USERS_ME);
  };

  const {
    mutate: signOut,
    mutateAsync: signOutAsync,
    isLoading,
  } = useMutation(client.users.logout, {
    onSuccess: async () => {
      performCleanup();
    },
    onError: async () => {
      performCleanup();
    },
    onSettled: () => {
      queryClient.clear();
    },
  });
  function handleLogout() {
    signOut();
  }
  return {
    mutate: handleLogout,
    signOutAsync,
    isLoading,
  };
}

export function useChangePassword() {
let [formError, setFormError] =
    useState<Partial<ChangePasswordUserInput> | null>(null);

  const { mutate, isLoading } = useMutation(client.users.changePassword, {
    onSuccess: (data) => {
      if (data) {
        setFormError({
          password: data?.message ?? '',
        });
        return;
      }
      toast.success(`${"Senha alterada com sucesso"}`);
    },
    onError: (error) => {
      const { response = {} }: any = error ?? {};
      const { data = {} } = response;

      const errorData = data.error || data;
      const message = errorData?.message;
      const details = errorData?.details;

      if (message) {
        toast.error(message);
      }

      if (details && Array.isArray(details) && details.length > 0) {
        const fieldErrors: any = {};
        details.forEach((detail: any) => {
          if (detail.field) {
            fieldErrors[detail.field] = [''];
          }
        });
        setFormError(fieldErrors);
      }
    },
  });

  return { mutate, isLoading, formError, setFormError };
}

export function useGetResetPasswordOptions() {
  let [formError, setFormError] = useState<string | null>(null);
  const { actions } = useStateMachine({ updateFormState });

  const { mutate, isLoading } = useMutation(client.users.getResetPasswordOptions, {
    onSuccess: (data, variables) => {
      const hasOptions =
        (data?.emails?.length ?? 0) > 0 || (data?.phones?.length ?? 0) > 0;
      if (!hasOptions) {
        setFormError('Dados não encontrados. Verifique ou cadastre-se para continuar.');
        return;
      }
      setFormError(null);
      actions.updateFormState({
        step: 'ContactOptions',
        cpf: variables.cpf,
        contactOptions: data,
      });
    },
    onError: (err: any) => {
      const data = err?.response?.data;
      const message =
        data?.error?.message ??
        data?.message ??
        'Não foi possível buscar as opções. Verifique o CPF.';
      setFormError(message);
    },
  });

  return { mutate, isLoading, formError, setFormError };
}

export function useForgotPassword() {
  const { actions } = useStateMachine({ updateFormState });
  let [message, setMessage] = useState<string | null>(null);
  let [formError, setFormError] = useState<any>(null);
const { mutate, isLoading } = useMutation(client.users.forgotPassword, {
    onSuccess: (data, variables) => {
      if (data?.message) {
        setFormError({ contactType: data?.message ?? '' });
        return;
      }
      actions.updateFormState({
        step: 'ConfirmedSent',
        selectedContactType: variables.contactType,
      });
    },
    onError: (err: any) => {
      const message =
        err?.response?.data?.message ?? 'Não foi possível enviar. Tente novamente.';
      setFormError({ contactType: message });
    },
  });

  return { mutate, isLoading, message, formError, setFormError, setMessage };
}

export function useResetPassword() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { closeModal } = useModalAction();
  const [_, setAuthorized] = useAtom(authorizationAtom);
  let [formError, setFormError] = useState<any>(null);

  const { mutate, isLoading } = useMutation(client.users.resetPassword, {
    onSuccess: async (data: any) => {
      // The API now returns an AuthResponse-like structure with token, refresh_token, permissions
      if (data?.token) {
        setAuthCredentials(
          data.token,
          data.permissions,
          data.refresh_token,
        );
        await runPostLoginCartSync();
        setAuthorized(true);

        // Netcore: User identity and profile will be set when user data is fetched via useUser hook
        // (AuthResponse doesn't include user data, so we wait for useUser to fetch it)
        // GTM login event will be sent from useUser hook when user data is available

        toast.success('Senha alterada com sucesso!');

        const redirectTarget = sessionStorage.getItem('redirect_after_login');
        if (redirectTarget) {
          sessionStorage.removeItem('redirect_after_login');
          router.push(redirectTarget);
        } else {
          router.push(Routes.home);
        }

        closeModal();
      } else {
        // Fallback: if no token in response, show login modal
        toast.success('Senha alterada com sucesso!');
        router.push(Routes.home);
      }
    },
    onSettled: () => {
      queryClient.clear();
    },
    onError: (error) => {
      const {
        response: { data },
      }: any = error ?? {};
      toast.error('Ocorreu um erro ao alterar a senha. Tente novamente.');
      setFormError(data);
    },
  });

  return { mutate, isLoading, formError };
}

export function useValidateResetToken() {
  let [error, setError] = useState<string | null>(null);

  const { mutate, isLoading } = useMutation(client.users.validateResetToken, {
    onSuccess: (data) => {
      if (data?.valid) {
        setError(null);
      } else {
        setError('Token inválido ou expirado');
      }
    },
    onError: (err: any) => {
      const { response = {} } = err ?? {};
      const { data = {} } = response;
      const errorData = data.error || data;
      const errorMessage = errorData?.message ?? 'Token inválido ou expirado';
      setError(errorMessage);
    },
  });

  return { mutate, isLoading, error, setError };
}

export function useVerifyForgotPasswordToken() {
  const { actions } = useStateMachine({ updateFormState });
  const queryClient = useQueryClient();
  let [formError, setFormError] = useState<any>(null);

  const { mutate, isLoading } = useMutation(
    client.users.verifyForgotPasswordToken,
    {
      onSuccess: (data, variables) => {
        if (!data.success) {
          setFormError({
            token: data?.message ?? '',
          });
          return;
        }
        actions.updateFormState({
          step: 'Password',
          token: variables.token as string,
        });
      },
      onSettled: () => {
        queryClient.clear();
      },
    },
  );

  return { mutate, isLoading, formError, setFormError };
}
